import { FocusMonitor } from '@angular/cdk/a11y';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  ViewChild
} from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material';
import { TranslocoService } from '@ngneat/transloco';
import { Observable, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { SignsAttachmentsModalComponent } from 'src/app/encounters/signs/signs-attachments-modal/signs-attachments-modal.component';
import { SignsAttachmentsService } from '../../../encounters/signs/signs-attachments-modal/signs-attachments.service';
import { DryEyeFormGroup } from '../../consult-forms/dry-eye-forms/dry-eye-form/dry-eye-form.model';
import { PreOpFormGroup } from '../../consult-forms/dry-eye-forms/pre-op/pre-op-form.model';
import { SymptomValidatorsService } from '../../consult-forms/validators/symptom-validators/symptom-validators.service';
import { leftEyeKey, rightEyeKey } from '../../form.model';
import { SchemaService } from '../services/schema/schema.service';
import { SymptomControlAdapterComponent } from '../symptom-control-adapter/symptom-control-adapter.component';
import { ThermalScaleService } from '../thermal-scale/thermal-scale.service';
import { Symptom, SymptomMethod, SymptomTypes } from '../symptom.model';
import {
  LEFT_EYE_KEY_PREFIX,
  RIGHT_EYE_KEY_PREFIX,
  SYMPTOM_METHOD_KEY_SUFFIX
} from './../../form.model';

@Component({
  selector: 'csi-symptom',
  templateUrl: './symptom.component.html',
  styleUrls: ['./symptom.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class SymptomComponent implements OnInit, OnDestroy, AfterViewInit {
  public readonly SymptomTypes = SymptomTypes;

  @Input() key: string;
  @Input() symptom: Symptom;
  @Input() formGroup: DryEyeFormGroup | PreOpFormGroup;
  @Input() isRequired: boolean;
  @Input() skipMethod: boolean;
  @Input() previousSignValues: { [key: string]: any };

  @ViewChild('leftSymptomControlAdapter', { static: false })
  leftSymptomControlAdapter: SymptomControlAdapterComponent;

  public showRequiredStar$: Observable<boolean>;
  public isInvalidLeft$: Observable<boolean>;
  public isInvalidRight$: Observable<boolean>;

  public isOptional = true;
  public selectedSignAttachmentsIndex = 0;

  public readonly RIGHT_EYE_KEY_PREFIX = RIGHT_EYE_KEY_PREFIX;
  public readonly LEFT_EYE_KEY_PREFIX = LEFT_EYE_KEY_PREFIX;

  private destroy$ = new Subject<void>();

  constructor(
    private focusMonitor: FocusMonitor,
    private hostElementRef: ElementRef,
    private symptomValidatorsService: SymptomValidatorsService,
    private signsAttachmentsService: SignsAttachmentsService,
    private dialog: MatDialog,
    private schemaService: SchemaService,
    private thermalScaleService: ThermalScaleService,
    private translocoService: TranslocoService,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit() {
    if (this.symptom.type === SymptomTypes.Method && !this.skipMethod) {
      this.assignQuantitativeMethodByDefault();
    }

    this.focusMonitor.monitor(this.hostElementRef, true);
    this.selectedSignAttachmentsIndex = Object.keys(
      this.signsAttachmentsService.signToFriendlyNameMap
    ).indexOf(this.key);

    // Subscribe to language changes to trigger re-rendering of pill text
    this.translocoService.langChanges$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.cdr.markForCheck();
    });

    // formGroup-level subscription catches modal controls created dynamically after ngOnInit
    if (this.formGroup) {
      this.formGroup.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(() => {
        this.cdr.markForCheck();
      });
    }
  }

  ngOnDestroy() {
    this.focusMonitor.stopMonitoring(this.hostElementRef);
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.isOptional = this.isControlOptional();
      this.cdr.markForCheck();
    });
  }

  get rightEyeFormControlName(): string {
    return rightEyeKey(this.key);
  }

  get leftEyeFormControlName(): string {
    return leftEyeKey(this.key);
  }

  showAttachmentsButton() {
    return this.signsAttachmentsService.signToFriendlyNameMap[this.key];
  }

  assignQuantitativeMethodByDefault() {
    const rightMethodControlKey = RIGHT_EYE_KEY_PREFIX + this.key + SYMPTOM_METHOD_KEY_SUFFIX;
    const leftMethodControlKey = LEFT_EYE_KEY_PREFIX + this.key + SYMPTOM_METHOD_KEY_SUFFIX;
    if (
      !this.formGroup.controls[rightMethodControlKey] &&
      this.formGroup.controls[this.rightEyeFormControlName] &&
      this.formGroup.controls[this.rightEyeFormControlName].value
    ) {
      this.formGroup.addControl(
        rightMethodControlKey,
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
      this.formGroup.controls[rightMethodControlKey].setValue(SymptomMethod.Qualitative);
    }

    if (
      !this.formGroup.controls[leftMethodControlKey] &&
      this.formGroup.controls[this.leftEyeFormControlName] &&
      this.formGroup.controls[this.leftEyeFormControlName].value
    ) {
      this.formGroup.addControl(
        leftMethodControlKey,
        new FormControl({ value: null, disabled: this.formGroup.disabled })
      );
      this.formGroup.controls[leftMethodControlKey].setValue(SymptomMethod.Qualitative);
    }
  }

  copyRightEyeValueToLeftEye() {
    const rightKeys = this.leftSymptomControlAdapter.allPossibleSymptomControlKeys;
    const keyValues = {};

    rightKeys.forEach(rightKey => {
      const leftKey = rightKey.replace(this.rightEyeFormControlName, this.leftEyeFormControlName);
      const leftControl = this.formGroup.get(leftKey);
      const rightControl = this.formGroup.get(rightKey);

      if (rightControl) {
        const rightValue = rightControl.value;
        if (!leftControl) {
          this.formGroup.addControl(
            leftKey,
            new FormControl({ value: rightValue, disabled: this.formGroup.disabled })
          );
        } else {
          keyValues[leftKey] = rightControl.value;
        }
      } else if (leftControl) {
        keyValues[leftKey] = '';
      }
    });

    this.patchValueUntilFormStopsChanging(keyValues);
  }

  private isControlOptional(): boolean {
    const leftEyeControl = this.formGroup.get(this.leftEyeFormControlName);
    return this.isRequired || this.symptomValidatorsService.isOptionalFormControl(leftEyeControl);
  }

  // TODO: econsult.component.ts has this same method. Maybe refactor and put it into
  // the FormGroup itself (extend FormGroup class).
  private patchValueUntilFormStopsChanging(
    value: {
      [key: string]: any;
    },
    previousJsonValue?: string
  ) {
    this.formGroup.patchValue(value);
    const currentJsonValue = JSON.stringify(this.formGroup.getRawValue());
    if (!previousJsonValue || currentJsonValue.localeCompare(previousJsonValue) !== 0) {
      setTimeout(() => this.patchValueUntilFormStopsChanging(value, currentJsonValue));
    }
  }

  get previousRightValue(): any {
    return this.previousSignValues && this.previousSignValues[this.rightEyeFormControlName];
  }

  get previousLeftValue(): any {
    return this.previousSignValues && this.previousSignValues[this.leftEyeFormControlName];
  }

  getPillDisplayText(side: 'right' | 'left'): string {
    const value = side === 'right' ? this.previousRightValue : this.previousLeftValue;
    if (value == null) return '';

    if (typeof value === 'object') {
      return 'Prev.';
    }

    if (typeof value === 'number') {
      const method = this.getPreviousMethod(side);
      const units = this.schemaService.getUnitsForSymptomKey(this.key, method);
      return units ? value + ' ' + units : String(value);
    }

    const display = this.translatePillValue(String(value), side);
    return display.length > 15 ? display.substring(0, 12) + '...' : display;
  }

  private translatePillValue(value: string, side: 'right' | 'left'): string {
    const activeLang = this.translocoService.getActiveLang();
    const translationObject = this.translocoService.getTranslation(activeLang);

    // Try simple path: signs.KEY.values.VALUE
    const simpleKey = `signs.${this.key}.values.${value}`;
    if (translationObject[simpleKey]) {
      return translationObject[simpleKey];
    }

    // Try with method: signs.KEY.METHOD.values.VALUE
    const method = this.getPreviousMethod(side);
    if (method) {
      const methodKey = `signs.${this.key}.${method}.values.${value}`;
      if (translationObject[methodKey]) {
        return translationObject[methodKey];
      }
    }

    // Fallback to schema friendly name
    const friendly = this.schemaService.getFriendlyValueName(value, this.key);
    if (typeof friendly === 'string' && friendly.indexOf('<') === -1) {
      return friendly;
    }

    // Last resort: return the raw value
    return value;
  }

  applyPreviousValue(side: 'right' | 'left') {
    const prefix = side === 'right' ? RIGHT_EYE_KEY_PREFIX : LEFT_EYE_KEY_PREFIX;
    const controlName =
      side === 'right' ? this.rightEyeFormControlName : this.leftEyeFormControlName;
    const value = side === 'right' ? this.previousRightValue : this.previousLeftValue;

    // Normalize the value before applying it
    const currentMethod = this.getCurrentMethod(side);
    const previousMethod = this.getPreviousMethod(side);
    const thermalConfig =
      this.schemaService.getThermalScaleConfig(this.key, currentMethod) ||
      this.schemaService.getThermalScaleConfig(this.key, previousMethod);

    let normalizedValue = value;
    if (thermalConfig) {
      normalizedValue = this.normalizeValue(value, thermalConfig);
    }

    const patchObj: { [key: string]: any } = { [controlName]: normalizedValue };

    const methodKey = prefix + this.key + SYMPTOM_METHOD_KEY_SUFFIX;
    const previousMethod2 = this.previousSignValues && this.previousSignValues[methodKey];

    if (previousMethod2 != null) {
      if (!this.formGroup.get(methodKey)) {
        this.formGroup.addControl(
          methodKey,
          new FormControl({ value: previousMethod2, disabled: this.formGroup.disabled })
        );
      } else {
        patchObj[methodKey] = previousMethod2;
      }
    }

    this.patchValueUntilFormStopsChanging(patchObj);

    // Force change detection after patching
    this.cdr.markForCheck();
  }

  /**
   * Normalize legacy value formats to current schema format
   * E.g., "grade1" -> "mild", "grade2" -> "moderate", etc.
   */
  private normalizeValue(value: any, thermalConfig: any): any {
    if (value == null) return value;

    const valueStr = String(value).toLowerCase();

    // Check if value already exists in thermal config
    const configValues = Object.values(thermalConfig).map(v => String(v).toLowerCase());
    if (configValues.includes(valueStr)) {
      return value;
    }

    // Handle legacy "gradeX" format
    const gradeMatch = valueStr.match(/^grade(\d+)$/);
    if (gradeMatch) {
      const gradeNum = parseInt(gradeMatch[1], 10);
      // Map grade numbers to severity levels
      // grade0 -> normal, grade1 -> mild, grade2 -> moderate, grade3/4 -> severe
      const gradeToSeverity: { [key: number]: string } = {
        0: 'normal',
        1: 'mild',
        2: 'moderate',
        3: 'severe',
        4: 'severe'
      };

      const mappedValue = gradeToSeverity[gradeNum];
      if (mappedValue) {
        // Find the actual value in thermalConfig that matches this severity
        for (const [, configValue] of Object.entries(thermalConfig)) {
          if (String(configValue).toLowerCase() === mappedValue) {
            return configValue;
          }
        }
      }
    }

    // Return original value if no normalization needed
    return value;
  }

  getValueTrend(side: 'right' | 'left'): 'up' | 'down' | null {
    const controlName =
      side === 'right' ? this.rightEyeFormControlName : this.leftEyeFormControlName;
    const control = this.formGroup.get(controlName);
    const currentValue = control && control.value;
    const previousValue = side === 'right' ? this.previousRightValue : this.previousLeftValue;

    if (currentValue == null || previousValue == null || currentValue === previousValue) {
      return null;
    }

    // Try numeric comparison first
    const current = parseFloat(String(currentValue));
    const previous = parseFloat(String(previousValue));

    if (!isNaN(current) && !isNaN(previous)) {
      if (current === previous) return null;
      return current > previous ? 'up' : 'down';
    }

    // For non-numeric values, use thermal scale severity comparison
    // Use current method for current value, previous method for previous value
    const currentMethod = this.getCurrentMethod(side);
    const previousMethod = this.getPreviousMethod(side);

    // Try to get thermal config - prefer current method, fallback to previous method
    const thermalConfig =
      this.schemaService.getThermalScaleConfig(this.key, currentMethod) ||
      this.schemaService.getThermalScaleConfig(this.key, previousMethod);

    if (!thermalConfig) {
      return null;
    }

    // Normalize values to handle legacy formats (e.g., "grade1" -> "mild")
    const normalizedCurrentValue = this.normalizeValue(currentValue, thermalConfig);
    const normalizedPreviousValue = this.normalizeValue(previousValue, thermalConfig);

    const currentSeverity = this.thermalScaleService.getColorValue(
      normalizedCurrentValue,
      thermalConfig
    );
    const previousSeverity = this.thermalScaleService.getColorValue(
      normalizedPreviousValue,
      thermalConfig
    );

    if (
      currentSeverity == null ||
      previousSeverity == null ||
      currentSeverity === previousSeverity
    ) {
      return null;
    }

    return currentSeverity > previousSeverity ? 'up' : 'down';
  }

  isTrendImprovement(side: 'right' | 'left'): boolean | null {
    const controlName =
      side === 'right' ? this.rightEyeFormControlName : this.leftEyeFormControlName;
    const control = this.formGroup.get(controlName);
    const currentValue = control && control.value;
    const previousValue = side === 'right' ? this.previousRightValue : this.previousLeftValue;

    if (currentValue == null || previousValue == null || currentValue === previousValue)
      return null;

    // Use current method for current value, previous method for previous value
    const currentMethod = this.getCurrentMethod(side);
    const previousMethod = this.getPreviousMethod(side);

    // Try to get thermal config - prefer current method, fallback to previous method
    const thermalConfig =
      this.schemaService.getThermalScaleConfig(this.key, currentMethod) ||
      this.schemaService.getThermalScaleConfig(this.key, previousMethod);
    if (!thermalConfig) return null;

    // Normalize values to handle legacy formats
    const normalizedCurrentValue = this.normalizeValue(currentValue, thermalConfig);
    const normalizedPreviousValue = this.normalizeValue(previousValue, thermalConfig);

    const currentSeverity = this.thermalScaleService.getColorValue(
      normalizedCurrentValue,
      thermalConfig
    );
    const previousSeverity = this.thermalScaleService.getColorValue(
      normalizedPreviousValue,
      thermalConfig
    );

    if (currentSeverity == null || previousSeverity == null || currentSeverity === previousSeverity)
      return null;

    return currentSeverity < previousSeverity;
  }

  private getPreviousMethod(side: 'right' | 'left'): SymptomMethod {
    if (!this.previousSignValues) return null;
    const prefix = side === 'right' ? RIGHT_EYE_KEY_PREFIX : LEFT_EYE_KEY_PREFIX;
    const methodKey = prefix + this.key + SYMPTOM_METHOD_KEY_SUFFIX;
    return this.previousSignValues[methodKey] || null;
  }

  private getCurrentMethod(side: 'right' | 'left'): SymptomMethod {
    const prefix = side === 'right' ? RIGHT_EYE_KEY_PREFIX : LEFT_EYE_KEY_PREFIX;
    const methodKey = prefix + this.key + SYMPTOM_METHOD_KEY_SUFFIX;
    const methodControl = this.formGroup.get(methodKey);
    return methodControl ? methodControl.value : null;
  }

  public openAttachmentsDialog() {
    const dialogRef = SignsAttachmentsModalComponent.open(
      this.dialog,
      this.selectedSignAttachmentsIndex
    );

    dialogRef.afterClosed().subscribe(() => {
      this.signsAttachmentsService.updateAttachments();
    });
  }
}
